/**
 * 用户管理页面（管理员视角）
 * 与后端API完整对接
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  X,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
  KeyRound,
  Shield,
} from 'lucide-react';
import { api, getErrorMessage } from '@/services/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import styles from './Users.module.css';

interface UserItem {
  user_id: number;
  user_name: string;
  account: string;
  role_id: number;
  role_name: string;
  status: number;
  dept_id?: number;
  dept_name?: string;
  phone?: string;
  create_time?: string;
}

interface Role {
  role_id: number;
  role_name: string;
}

interface Department {
  dept_id: number;
  dept_name: string;
}

interface UserFormData {
  user_name: string;
  account: string;
  role_id: number;
  dept_id: number;
}

const initialFormData: UserFormData = {
  user_name: '',
  account: '',
  role_id: 3,
  dept_id: 0,
};

interface EditFormData {
  user_name: string;
  dept_id: number;
  phone: string;
  role_name: string;
  status: number;
  max_view_level: number;
}

const initialEditFormData: EditFormData = {
  user_name: '',
  dept_id: 0,
  phone: '',
  role_name: '',
  status: 1,
  max_view_level: 1,
};

export default function UsersPage() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 角色编辑弹窗
  const [roleEditModal, setRoleEditModal] = useState(false);
  const [roleEditUser, setRoleEditUser] = useState<UserItem | null>(null);
  const [roleEditSelections, setRoleEditSelections] = useState<number[]>([]);
  const [roleEditSaving, setRoleEditSaving] = useState(false);
  const [roleEditFunctions, setRoleEditFunctions] = useState<Record<string, Array<{role_id:number;role_name:string}>>>({});

  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');

  // 分页状态
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  // 批量操作状态
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [newUserRoles, setNewUserRoles] = useState<number[]>([]);
  const [newUserRoleGroups, setNewUserRoleGroups] = useState<Record<string, Array<{role_id:number;role_name:string}>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    type: 'toggle' | 'delete' | 'resetPassword';
    user: UserItem | null;
  }>({ show: false, type: 'toggle', user: null });
  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState<UserItem | null>(null);
  const [editFormData, setEditFormData] = useState<EditFormData>(initialEditFormData);
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchConfirmModal, setBatchConfirmModal] = useState(false);

  // 加载用户列表和角色列表
  useEffect(() => {
    loadData();
  }, [currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 获取所有用户，不分页（为了前端过滤）
      const [usersRes, rolesRes, deptsRes] = await Promise.all([
        api.getUsers({ page: 1, page_size: 100 }), // 获取所有用户（后端最大允许100）
        api.getUserRoles(),
        api.getDepartments(),
      ]);

      setUsers(usersRes.data || []); // usersRes.data is User[]
      if (rolesRes.data) {
        setRoles(rolesRes.data);
      }
      if (deptsRes.data) {
        setDepartments(deptsRes.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 过滤用户（隐藏超级管理员）
  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortTh = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th className={styles.tableHeaderCell} style={{cursor:'pointer',userSelect:'none'}} onClick={() => handleSort(field)}>
      {children} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const filteredUsers = users.filter((user) => {
    if (user.role_name === '超级管理员' || user.role_name === '管理员' || user.role_name === '客户') return false;
    const matchSearch = user.user_name.toLowerCase().includes(searchQuery.toLowerCase()) || user.account.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = filterRole === 'all' || user.role_name === filterRole;
    return matchSearch && matchRole;
  });

  if (sortField) {
    filteredUsers.sort((a: any, b: any) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? '';
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  // 计算总页数
  useEffect(() => {
    const totalItems = filteredUsers.length;
    const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage);
    setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);
  }, [filteredUsers, itemsPerPage]);

  // 当前页的用户数据
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getRoleBadgeClass = (roleId: number) => {
    switch (roleId) {
      case 1:
        return styles.admin;
      case 2:
        return styles.teacher;
      case 3:
        return styles.student;
      default:
        return '';
    }
  };

  // 打开创建用户弹窗
  const handleOpenModal = async () => {
    setFormData(initialFormData);
    setFormError(null);
    setNewUserRoles([]);
    // 加载角色分组
    try {
      const res = await api.getRoles();
      const allRoles: any[] = res.data || [];
      const cfgRes = await api.request<{enabled_systems:string[];enabled_roles:Array<{system_key:string;role_id:number;role_name:string}>}>('/system-config/enabled');
      const enabledSystems = cfgRes.data?.enabled_systems || [];
      const enabledRoleIds = new Set((cfgRes.data?.enabled_roles || []).map(r => r.role_id));
      const ROLE_ORDER: Record<string, string[]> = { exam:['学生','教师','考官'], work:['业务','领导'], share:['销售','研发','采购'], knowledge:['学员','专家'], drawing:['图纸管理员','图纸研发'] };
      const grouped: Record<string, Array<{role_id:number;role_name:string}>> = {};
      allRoles.forEach((r: any) => {
        const fn = r.function || 'exam';
        if (!enabledSystems.includes(fn)) return;
        if (!enabledRoleIds.has(r.role_id)) return;
        if ((ROLE_ORDER[fn]||[]).includes(r.role_name)) {
          if (!grouped[fn]) grouped[fn] = [];
          grouped[fn].push({role_id:r.role_id, role_name:r.role_name});
        }
      });
      setNewUserRoleGroups(grouped);
    } catch { setNewUserRoleGroups({}); }
    setShowModal(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setShowModal(false);
    setFormData(initialFormData);
    setFormError(null);
  };

  // 表单字段变化
  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'role_id' || name === 'dept_id' ? Number(value) : value,
    }));
  };

  // 提交创建用户
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // 验证
    if (!formData.user_name.trim()) {
      setFormError('请输入用户名');
      return;
    }
    if (!formData.account.trim()) {
      setFormError('请输入账号');
      return;
    }
    if (!formData.dept_id) {
      setFormError('请选择部门');
      return;
    }

    try {
      setSubmitting(true);

      const selectedRole = roles.find((r) => r.role_id === formData.role_id);
      await api.createUser({
        user_name: formData.user_name.trim(),
        account: formData.account.trim(),
        password: '123456', // 默认密码
        role_name: '学生',
        dept_id: formData.dept_id,
      });

      // 保存角色选择
      if (newUserRoles.length > 0) {
        try {
          await api.request(`/users/${formData.account.trim()}/function-roles`, {
            method: 'PUT', body: JSON.stringify({ role_ids: newUserRoles }),
          });
        } catch {}
      }

      // 重新加载用户列表
      await loadData();
      handleCloseModal();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '创建用户失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 删除用户
  const handleDelete = async (user: UserItem) => {
    setConfirmModal({ show: true, type: 'delete', user });
  };

  // 切换用户状态
  const handleToggleStatus = (user: UserItem) => {
    setConfirmModal({ show: true, type: 'toggle', user });
  };

  // 确认删除用户
  const confirmDelete = async () => {
    const user = confirmModal.user;
    if (!user) return;

    try {
      setDeleting(user.account);
      await api.deleteUser(user.account);
      setUsers((prev) => prev.filter((u) => u.account !== user.account));
      setConfirmModal({ show: false, type: 'delete', user: null });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeleting(null);
    }
  };

  // 确认切换状态
  const confirmToggleStatus = async () => {
    const user = confirmModal.user;
    if (!user) return;

    try {
      setToggling(user.account);
      const response = await api.toggleUserActive(user.account);

      if (response.data) {
        setUsers((prev) =>
          prev.map((u) =>
            u.account === user.account
              ? { ...u, status: response.data!.status }
              : u
          )
        );
      }
      setConfirmModal({ show: false, type: 'toggle', user: null });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '切换状态失败');
    } finally {
      setToggling(null);
    }
  };

  // 打开重置密码确认
  const handleResetPassword = (user: UserItem) => {
    setConfirmModal({ show: true, type: 'resetPassword', user });
  };

  // 确认重置密码
  const confirmResetPassword = async () => {
    const user = confirmModal.user;
    if (!user) return;

    try {
      setResettingPassword(true);
      await api.resetPassword(user.account);
      setConfirmModal({ show: false, type: 'resetPassword', user: null });
      showToast('success', `用户「${user.user_name}」的密码已重置为 123456`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '重置密码失败');
    } finally {
      setResettingPassword(false);
    }
  };

  const closeConfirmModal = () => setConfirmModal({ show: false, type: 'toggle', user: null });

  // 打开批量删除确认弹窗
  const handleBatchDelete = () => {
    if (selectedUserIds.length === 0) return;
    setBatchConfirmModal(true);
  };

  // 确认批量删除
  const confirmBatchDelete = async () => {
    try {
      setBatchDeleting(true);

      // 获取选中用户的账号列表
      const selectedAccounts = users
        .filter(user => selectedUserIds.includes(user.user_id))
        .map(user => user.account);

      const results = await api.batchDeleteUsers(selectedAccounts);

      if (results.failed.length === 0) {
        showToast('success', `成功删除 ${results.success.length} 个用户`);
      } else if (results.success.length === 0) {
        showToast('error', `删除失败：${results.failed[0].error}`);
      } else {
        showToast('warning', `成功删除 ${results.success.length} 个用户，${results.failed.length} 个失败`);
      }

      // 刷新用户列表
      await loadData();
      setSelectedUserIds([]);
      setBatchConfirmModal(false);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : '批量删除失败');
    } finally {
      setBatchDeleting(false);
    }
  };

  // ===== 角色编辑 =====
  const ROLE_ORDER: Record<string, string[]> = {
    exam: ['学生', '教师', '考官'],
    work: ['业务', '领导'],
    share: ['销售', '研发', '采购'],
    knowledge: ['学员', '专家'],
    admin: ['管理员'],
    drawing: ['图纸管理员', '图纸研发'],
  };
  const ROLE_LABELS: Record<string, string> = {
    exam: '学习考试', work: '日常工作', share: '分享平台', knowledge: '知识传承', admin: '管理员', drawing: '图纸管理',
  };

  const handleOpenRoleEdit = async (user: UserItem) => {
    setRoleEditUser(user);
    try {
      // 获取系统配置（哪些系统/角色启用了）
      const cfgRes = await api.request<{enabled_systems:string[];enabled_roles:Array<{system_key:string;role_id:number;role_name:string}>}>('/system-config/enabled');
      const enabledSystems = cfgRes.data?.enabled_systems || [];
      const enabledRoleIds = new Set((cfgRes.data?.enabled_roles || []).map(r => r.role_id));

      const res = await api.getRoles();
      const allRoles: any[] = res.data || [];
      const grouped: Record<string, Array<{role_id:number;role_name:string}>> = {};
      allRoles.forEach((r: any) => {
        const fn = r.function || 'exam';
        const allowed = ROLE_ORDER[fn] || [];
        // 只显示启用的系统和角色
        if (!enabledSystems.includes(fn)) return;
        if (!enabledRoleIds.has(r.role_id)) return;
        if (allowed.includes(r.role_name) && !grouped[fn]) grouped[fn] = [];
        if (allowed.includes(r.role_name)) grouped[fn].push({ role_id: r.role_id, role_name: r.role_name });
      });
      const sorted: Record<string, Array<{role_id:number;role_name:string}>> = {};
      for (const fn of Object.keys(ROLE_LABELS)) {
        if (grouped[fn]) {
          sorted[fn] = grouped[fn].sort((a, b) => ROLE_ORDER[fn].indexOf(a.role_name) - ROLE_ORDER[fn].indexOf(b.role_name));
        }
      }
      setRoleEditFunctions(sorted);
      const frRes = await api.request<Array<{id:number;role_id:number;role_name:string;function:string}>>(`/users/${user.account}/function-roles`);
      setRoleEditSelections((frRes.data || []).map((r: any) => r.role_id));
    } catch { setRoleEditFunctions({}); setRoleEditSelections([]); }
    setRoleEditModal(true);
  };

  const handleToggleRole = (fnKey: string, roleId: number) => {
    setRoleEditSelections(prev => {
      // 获取该功能下的所有角色 ID
      const fnRoleIds = (roleEditFunctions[fnKey] || []).map(r => r.role_id);
      // 移除该功能下已选的角色，加入新选的
      const withoutFn = prev.filter(id => !fnRoleIds.includes(id));
      return prev.includes(roleId) ? withoutFn : [...withoutFn, roleId];
    });
  };

  const handleSaveRoles = async () => {
    if (!roleEditUser) return;
    setRoleEditSaving(true);
    try {
      await api.request(`/users/${roleEditUser.account}/function-roles`, {
        method: 'PUT',
        body: JSON.stringify({ role_ids: roleEditSelections }),
      });
      showToast('success', '角色更新成功');
      setRoleEditModal(false);
      loadData();
    } catch (err) {
      showToast('error', getErrorMessage(err));
    } finally { setRoleEditSaving(false); }
  };

  // 打开编辑弹窗
  const handleOpenEditModal = (user: UserItem) => {
    setEditUser(user);
    // 根据部门名称查找对应的部门ID，如果没有找到则使用默认值
    const deptId = departments.find(dept => dept.dept_name === user.dept_name)?.dept_id || 0;
    setEditFormData({
      user_name: user.user_name,
      dept_id: user.dept_id ?? deptId,
      phone: user.phone || '',
      role_name: user.role_name,
      status: user.status,
      max_view_level: user.max_view_level || 1,
    });
    setEditFormError(null);
    setEditModal(true);
  };

  // 关闭编辑弹窗
  const handleCloseEditModal = () => {
    setEditModal(false);
    setEditUser(null);
    setEditFormData(initialEditFormData);
    setEditFormError(null);
  };

  // 编辑表单字段变化
  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: (name === 'dept_id' || name === 'max_view_level' || name === 'status') ? Number(value) : value,
    }));
  };

  // 提交编辑
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;

    setEditFormError(null);

    // 验证
    if (!editFormData.user_name.trim()) {
      setEditFormError('请输入用户名');
      return;
    }
    if (!editFormData.dept_id) {
      setEditFormError('请选择部门');
      return;
    }
    // 手机号验证（如果填写了）
    if (editFormData.phone && !/^1[3-9]\d{9}$/.test(editFormData.phone)) {
      setEditFormError('请输入正确的手机号');
      return;
    }

    try {
      setEditSubmitting(true);
      await api.updateUser(editUser.account, {
        user_name: editFormData.user_name.trim(),
        dept_id: editFormData.dept_id,
        phone: editFormData.phone.trim() || null,
        role_name: editFormData.role_name,
        status: editFormData.status,
        max_view_level: editFormData.max_view_level,
      } as any);

      // 更新本地列表
      const selectedRole = roles.find((r) => r.role_name === editFormData.role_name);
      const selectedDept = departments.find((d) => d.dept_id === editFormData.dept_id);
      setUsers((prev) =>
        prev.map((u) =>
          u.account === editUser.account
            ? {
                ...u,
                user_name: editFormData.user_name.trim(),
                dept_id: editFormData.dept_id,
                dept_name: selectedDept?.dept_name || '',
                phone: editFormData.phone.trim() || undefined,
                role_name: editFormData.role_name,
                role_id: selectedRole?.role_id || u.role_id,
                status: editFormData.status,
                max_view_level: editFormData.max_view_level,
              }
            : u
        )
      );
      handleCloseEditModal();
    } catch (err) {
      setEditFormError(err instanceof Error ? err.message : '更新失败');
    } finally {
      setEditSubmitting(false);
    }
  };

  // 加载状态
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinner} size={32} />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <span>{error}</span>
          <button onClick={loadData} className={styles.retryButton}>
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>用户管理</h1>
          <p className={styles.subtitle}>管理系统用户和权限</p>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className={styles.searchBar}>
        <div className={styles.searchInput}>
          <Search size={18} className={styles.searchIcon} strokeWidth={1.5} />
          <input
            type="text"
            placeholder="搜索用户名或账号..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className={styles.clearSearch}
              onClick={() => setSearchQuery('')}
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className={styles.filters}>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className={styles.roleSelect}
          >
            <option value="all">全部角色</option>
            {roles.map((role) => (
              <option key={role.role_id} value={role.role_name}>
                {role.role_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 批量操作工具栏 */}
      {selectedUserIds.length > 0 && (
        <div className={styles.batchToolbar}>
          <div className={styles.batchInfo}>
            <span>已选 {selectedUserIds.length} 项</span>
          </div>
          <div className={styles.batchActions}>
            <button
              className={styles.cancelBatchBtn}
              onClick={() => setSelectedUserIds([])}
            >
              取消选择
            </button>
            <button
              className={styles.batchDeleteBtn}
              onClick={handleBatchDelete}
              disabled={batchDeleting}
            >
              {batchDeleting ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  删除中...
                </>
              ) : (
                <>批量删除</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* 用户列表 */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeaderCell} style={{ width: '1%' }}>
                <div className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    checked={paginatedUsers.length > 0 && selectedUserIds.length === paginatedUsers.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // 选中当前页所有用户
                        const currentPageUserIds = paginatedUsers.map(user => user.user_id);
                        setSelectedUserIds(currentPageUserIds);
                      } else {
                        // 取消选中当前页所有用户
                        const currentPageUserIds = paginatedUsers.map(user => user.user_id);
                        setSelectedUserIds(prev => prev.filter(id => !currentPageUserIds.includes(id)));
                      }
                    }}
                  />
                  <div className={styles.checkboxCustom}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
              </th>
              <th className={styles.tableHeaderCell}>用户名称</th>
              <th className={styles.tableHeaderCell}>账号</th>
              <SortTh field="dept_name">部门</SortTh>
              <SortTh field="status">状态</SortTh>
              <SortTh field="create_time">创建时间</SortTh>
              <th className={`${styles.tableHeaderCell} ${styles.centerCell}`}>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => (
              <motion.tr
                key={user.user_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={styles.tableRow}
              >
                <td className={`${styles.tableCell} ${styles.checkbox}`}>
                  <div className={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUserIds(prev => [...prev, user.user_id]);
                        } else {
                          setSelectedUserIds(prev => prev.filter(id => id !== user.user_id));
                        }
                      }}
                    />
                    <div className={styles.checkboxCustom}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                </td>
                <td className={styles.tableCell} data-label="用户名称">
                  <span className={styles.userName}>{user.user_name}</span>
                </td>
                <td className={styles.tableCell} data-label="账号">{user.account}</td>
                <td className={styles.tableCell} data-label="部门">{user.dept_name || '-'}</td>
                <td className={styles.tableCell} data-label="状态">
                  <button
                    className={`${styles.statusToggle} ${
                      user.status === 1 ? styles.active : styles.inactive
                    }`}
                    onClick={() => handleToggleStatus(user)}
                    disabled={toggling === user.account}
                  >
                    {toggling === user.account ? (
                      <Loader2 size={12} className={styles.spinner} />
                    ) : user.status === 1 ? (
                      '正常'
                    ) : (
                      '禁用'
                    )}
                  </button>
                </td>
                <td className={styles.tableCell} data-label="创建时间">{user.create_time ? new Date(user.create_time).toLocaleDateString('zh-CN') : '-'}</td>
                <td className={`${styles.tableCell} ${styles.centerCell} ${styles.cardFooterCell}`} data-label="操作">
                  <div className={styles.tableActions}>
                    <button
                      className={styles.tableActionBtn}
                      title="编辑"
                      onClick={() => handleOpenEditModal(user)}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className={styles.tableActionBtn}
                      title="角色编辑"
                      onClick={() => handleOpenRoleEdit(user)}
                    >
                      <Shield size={16} />
                    </button>
                    <button
                      className={styles.tableActionBtn}
                      title="重置密码"
                      onClick={() => handleResetPassword(user)}
                      disabled={resettingPassword}
                    >
                      <KeyRound size={16} />
                    </button>
                    <button
                      className={`${styles.tableActionBtn} ${styles.danger}`}
                      onClick={() => handleDelete(user)}
                      disabled={deleting === user.account}
                      title="删除"
                    >
                      {deleting === user.account ? (
                        <Loader2 size={16} className={styles.spinner} />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 翻页控件 */}
      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          共 {filteredUsers.length} 条记录
        </div>
        <div className={styles.paginationControls}>
          <button
            className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            首页
          </button>
          <button
            className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            上一页
          </button>

          {/* 页码按钮 */}
          <div className={styles.pageNumbers}>
            {[...Array(Math.min(5, totalPages))].map((_, idx) => {
              let pageNum;
              if (totalPages <= 5) {
                // 总页数不超过5页，显示所有页码
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                // 当前页在前3页，显示前5页
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                // 当前页在后3页，显示后5页
                pageNum = totalPages - 4 + idx;
              } else {
                // 当前页在中间，显示当前页前后各2页
                pageNum = currentPage - 2 + idx;
              }

              if (pageNum > totalPages) return null;

              return (
                <button
                  key={pageNum}
                  className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                {currentPage > 3 && <span className={styles.pageEllipsis}>...</span>}
                <button
                  className={`${styles.pageNumber} ${currentPage === totalPages ? styles.active : ''}`}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          <button
            className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            下一页
          </button>
          <button
            className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            末页
          </button>
        </div>
      </div>

      {/* 统计（排除超级管理员） */}
      {/* 过滤出非超级管理员用户用于统计 */}
      {(() => {
        const displayUsers = users.filter(u => u.role_name !== '超级管理员');
        return (
          <div className={styles.stats}>
            <span className={styles.statItem}>
              共 <strong>{displayUsers.length}</strong> 个用户
            </span>
            <span className={styles.statItem}>
              管理员: <strong>{displayUsers.filter((u) => u.role_id === 1).length}</strong>
            </span>
            <span className={styles.statItem}>
              教师: <strong>{displayUsers.filter((u) => u.role_id === 2).length}</strong>
            </span>
            <span className={styles.statItem}>
              学生: <strong>{displayUsers.filter((u) => u.role_id === 3).length}</strong>
            </span>
          </div>
        );
      })()}

      {/* 创建用户弹窗 */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2>添加用户</h2>
                <button className={styles.closeButton} onClick={handleCloseModal}>
                  <X size={20} />
                </button>
              </div>

              <form className={styles.modalBody} onSubmit={handleSubmit}>
                <div className={styles.formHint}>
                  新用户默认密码为 <strong>123456</strong>，用户可在登录后修改
                </div>
                {formError && (
                  <div className={styles.formError}>{formError}</div>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="user_name">用户名 *</label>
                  <input
                    type="text"
                    id="user_name"
                    name="user_name"
                    value={formData.user_name}
                    onChange={handleFormChange}
                    placeholder="请输入用户名"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="account">账号 *</label>
                  <input
                    type="text"
                    id="account"
                    name="account"
                    value={formData.account}
                    onChange={handleFormChange}
                    placeholder="请输入登录账号"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>功能角色 <span style={{fontSize:'.6875rem',color:'#9ca3af',fontWeight:400}}>（每个功能选一个，可不选）</span></label>
                  <div style={{background:'#faf9f6',border:'1px solid #f0ebe4',borderRadius:12,padding:'10px 16px'}}>
                    {Object.entries(newUserRoleGroups).map(([fnKey, fnRoles]) => {
                      const labels: Record<string,string> = {exam:'学习考试',knowledge:'知识传承',share:'分享平台',work:'日常工作',drawing:'图纸管理'};
                      return (
                        <div key={fnKey} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0'}}>
                          <span style={{fontSize:'.8125rem',fontWeight:600,color:'var(--ink-600)',minWidth:72,flexShrink:0}}>{labels[fnKey]||fnKey}</span>
                          {fnRoles.map(role => {
                            const checked = newUserRoles.includes(role.role_id);
                            return (
                              <label key={role.role_id} onClick={() => setNewUserRoles(prev => {
                                const fnRoleIds = fnRoles.map(r => r.role_id);
                                const withoutFn = prev.filter(id => !fnRoleIds.includes(id));
                                return prev.includes(role.role_id) ? withoutFn : [...withoutFn, role.role_id];
                              })} style={{
                                padding:'3px 14px',borderRadius:16,cursor:'pointer',fontSize:'.8125rem',fontWeight:checked?500:400,
                                border:`1.5px solid ${checked?'var(--cinnabar-400)':'#e5e0d8'}`,background:checked?'#fef3f2':'white',
                                color:checked?'var(--cinnabar-600)':'var(--ink-500)',transition:'all .15s',userSelect:'none',
                              }}>
                                {role.role_name}
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                    {Object.keys(newUserRoleGroups).length===0 && <span style={{fontSize:'.75rem',color:'#9ca3af'}}>加载中...</span>}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="dept_id">部门 *</label>
                  <select
                    id="dept_id"
                    name="dept_id"
                    value={formData.dept_id}
                    onChange={handleFormChange}
                  >
                    <option value="0">请选择部门</option>
                    {departments.map((dept) => (
                      <option key={dept.dept_id} value={dept.dept_id}>
                        {dept.dept_name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCloseModal}
                >
                  取消
                </button>
                <button
                  type="button"
                  className={styles.submitButton}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className={styles.spinner} />
                      创建中...
                    </>
                  ) : (
                    '创建用户'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 确认弹窗 */}
      <AnimatePresence>
        {confirmModal.show && confirmModal.user && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeConfirmModal}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmIcon}>
                <AlertTriangle size={40} />
              </div>
<h3 className={styles.confirmTitle}>
                {confirmModal.type === 'delete'
                  ? '确认删除'
                  : confirmModal.type === 'resetPassword'
                    ? '确认重置密码'
                    : '确认操作'}
              </h3>
              <p className={styles.confirmMessage}>
                {confirmModal.type === 'delete'
                  ? <>
                      <div>确定要删除用户「{confirmModal.user?.user_name}」吗？</div>
                      <div style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
                        此操作将同时删除该用户创建的所有数据：
                      </div>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px', color: '#888', fontSize: '13px', lineHeight: '1.6' }}>
                        <li>上传的知识档案及文件</li>
                        <li>创建的考试任务及学生答题记录</li>
                        <li>创建的学习任务及学习记录</li>
                        <li>创建的试卷（未被他人引用的）</li>
                        <li>该用户的所有答题和学习记录</li>
                      </ul>
                      <div style={{ marginTop: '12px', color: 'var(--cinnabar-600)', fontWeight: '500' }}>
                        此操作不可撤销！
                      </div>
                    </>
                  : confirmModal.type === 'resetPassword'
                    ? <>
                        确定要将用户「{confirmModal.user?.user_name}」的密码重置为
                        <strong style={{ color: 'var(--cinnabar-600)', margin: '0 4px' }}>123456</strong>
                        吗？
                      </>
                    : confirmModal.user?.status === 1
                      ? `确定要禁用用户「{confirmModal.user?.user_name}」吗？禁用后该用户将无法登录。`
                      : `确定要启用用户「{confirmModal.user?.user_name}」吗？`}
              </p>
              <div className={styles.confirmActions}>
                <button
                  className={styles.confirmCancel}
                  onClick={closeConfirmModal}
                >
                  取消
                </button>
                <button
                  className={`${styles.confirmButton} ${
                    confirmModal.type === 'delete' ? styles.danger : ''
                  }`}
                  onClick={
                    confirmModal.type === 'delete'
                      ? confirmDelete
                      : confirmModal.type === 'resetPassword'
                        ? confirmResetPassword
                        : confirmToggleStatus
                  }
                  disabled={
                    deleting === confirmModal.user?.account ||
                    toggling === confirmModal.user?.account ||
                    resettingPassword
                  }
                >
                  {deleting === confirmModal.user?.account ||
                    toggling === confirmModal.user?.account ||
                    resettingPassword ? (
                    <Loader2 size={16} className={styles.spinner} />
                  ) : confirmModal.type === 'delete' ? (
                    '确认删除'
                  ) : confirmModal.type === 'resetPassword' ? (
                    '确认重置'
                  ) : confirmModal.user?.status === 1 ? (
                    '确认禁用'
                  ) : (
                    '确认启用'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 编辑用户弹窗 */}
      <AnimatePresence>
        {editModal && editUser && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseEditModal}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2>编辑用户</h2>
                <button className={styles.closeButton} onClick={handleCloseEditModal}>
                  <X size={20} />
                </button>
              </div>

              <form className={styles.modalBody} onSubmit={handleEditSubmit}>
                {editFormError && (
                  <div className={styles.formError}>{editFormError}</div>
                )}

                <div className={styles.formGrid}>
                  <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                    <label>账号</label>
                    <input
                      type="text"
                      value={editUser.account}
                      disabled
                      className={styles.disabledInput}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="edit_user_name">用户名 *</label>
                    <input
                      type="text"
                      id="edit_user_name"
                      name="user_name"
                      value={editFormData.user_name}
                      onChange={handleEditFormChange}
                      placeholder="请输入用户名"
                    />
                  </div>


                  <div className={styles.formGroup}>
                    <label htmlFor="edit_status">状态 *</label>
                    <select
                      id="edit_status"
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditFormChange}
                    >
                      <option value={1}>正常</option>
                      <option value={0}>禁用</option>
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="edit_dept_id">部门 *</label>
                    <select
                      id="edit_dept_id"
                      name="dept_id"
                      value={editFormData.dept_id}
                      onChange={handleEditFormChange}
                    >
                      <option value="0">请选择部门</option>
                      {departments.map((dept) => (
                        <option key={dept.dept_id} value={dept.dept_id}>
                          {dept.dept_name}
                        </option>
                      ))}
                    </select>
                  </div>


                  <div className={styles.formGroup}>
                    <label htmlFor="edit_phone">手机号</label>
                    <input
                      type="tel"
                      id="edit_phone"
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleEditFormChange}
                      placeholder="请输入手机号（选填）"
                      maxLength={11}
                    />
                  </div>
                </div>
              </form>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCloseEditModal}
                >
                  取消
                </button>
                <button
                  type="button"
                  className={styles.submitButton}
                  onClick={handleEditSubmit}
                  disabled={editSubmitting}
                >
                  {editSubmitting ? (
                    <>
                      <Loader2 size={16} className={styles.spinner} />
                      保存中...
                    </>
                  ) : (
                    '保存'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量删除确认弹窗 */}
      <AnimatePresence>
        {batchConfirmModal && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setBatchConfirmModal(false)}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmIcon}>
                <AlertTriangle size={40} />
              </div>
              <h3 className={styles.confirmTitle}>确认批量删除</h3>
              <p className={styles.confirmMessage}>
                <>
                  <div>确定要删除选中的 <strong>{selectedUserIds.length}</strong> 个用户吗？</div>
                  <div style={{ marginTop: '12px', color: '#666', fontSize: '14px' }}>
                    此操作将同时删除这些用户创建的所有数据：
                  </div>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px', color: '#888', fontSize: '13px', lineHeight: '1.6' }}>
                    <li>教师用户：知识档案、考试任务、学习任务、试卷等</li>
                    <li>学生用户：答题记录、学习记录等</li>
                    <li>管理员用户：所有相关数据</li>
                  </ul>
                  <div style={{ marginTop: '12px', color: 'var(--cinnabar-600)', fontWeight: '500' }}>
                    此操作不可撤销！
                  </div>
                </>
              </p>
              <div className={styles.confirmActions}>
                <button
                  className={styles.confirmCancel}
                  onClick={() => setBatchConfirmModal(false)}
                >
                  取消
                </button>
                <button
                  className={`${styles.confirmButton} ${styles.danger}`}
                  onClick={confirmBatchDelete}
                  disabled={batchDeleting}
                >
                  {batchDeleting ? (
                    <Loader2 size={16} className={styles.spinner} />
                  ) : (
                    '确认删除'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 角色编辑弹窗 */}
      <AnimatePresence>
        {roleEditModal && roleEditUser && (
          <motion.div className={styles.modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={() => setRoleEditModal(false)}>
            <motion.div className={styles.modal} style={{maxWidth:520}} initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0,scale:0.95}} onClick={e => e.stopPropagation()}>
              <div style={{padding:'16px 20px',borderBottom:'1px solid #f0ebe4',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <h2 style={{fontSize:'1rem',fontWeight:700}}>角色编辑 — {roleEditUser.user_name}</h2>
                <button onClick={() => setRoleEditModal(false)} style={{background:'none',border:'none',color:'#a3a3a3',cursor:'pointer'}}>✕</button>
              </div>
              <div style={{padding:'16px 20px',maxHeight:'60vh',overflow:'auto'}}>
                {Object.entries(roleEditFunctions).map(([fnKey, fnRoles]) => {
                  const selectedInFn = fnRoles.filter(r => roleEditSelections.includes(r.role_id));
                  return (
                    <div key={fnKey} style={{marginBottom:16}}>
                      <div style={{fontSize:'.8125rem',fontWeight:600,color:'var(--ink-600)',marginBottom:6}}>
                        {ROLE_LABELS[fnKey] || fnKey} {selectedInFn.length > 0 && <span style={{color:'var(--cinnabar-500)',fontSize:'.75rem'}}>({selectedInFn.map(r=>r.role_name).join(',')})</span>}
                      </div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                        {fnRoles.map(role => {
                          const checked = roleEditSelections.includes(role.role_id);
                          return (
                            <label key={role.role_id} style={{
                              display:'inline-flex',alignItems:'center',gap:4,padding:'6px 12px',
                              borderRadius:8,border:`1.5px solid ${checked ? 'var(--cinnabar-500)' : '#e5e7eb'}`,
                              background:checked?'#fef3f2':'white',cursor:'pointer',fontSize:'.8125rem',
                              fontWeight:checked?600:400,color:checked?'var(--cinnabar-600)':'var(--ink-600)',
                            }}>
                              <input type="checkbox" checked={checked} onChange={() => handleToggleRole(fnKey, role.role_id)} style={{display:'none'}} />
                              {role.role_name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(roleEditFunctions).length === 0 && <p style={{color:'#9ca3af',textAlign:'center',padding:20}}>加载角色中...</p>}
              </div>
              <div style={{padding:'12px 20px',borderTop:'1px solid #f0ebe4',display:'flex',justifyContent:'flex-end',gap:8}}>
                <button onClick={() => setRoleEditModal(false)} style={{padding:'8px 16px',border:'1px solid #e8e2d8',borderRadius:8,background:'white',cursor:'pointer'}}>取消</button>
                <button onClick={handleSaveRoles} disabled={roleEditSaving} style={{padding:'8px 20px',border:'none',borderRadius:8,background:roleEditSaving?'#c7d2fe':'var(--cinnabar-500)',color:'white',cursor:'pointer',fontWeight:500}}>
                  {roleEditSaving ? '保存中...' : '保存'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 浮动新增按钮 */}
      <motion.button className={styles.fab} onClick={handleOpenModal} whileHover={{scale:1.1}} whileTap={{scale:0.9}} title="添加用户">
        <Plus size={28} strokeWidth={2} />
      </motion.button>
    </div>
  );
}